import { create } from "zustand";
import { api } from "../api/greenclub";
import { useAuthStore } from "./auth"; // Importar o store de autenticação

// Store de produtos integrado com backend
export const useProdutosStore = create((set, get) => ({
  produtos: [],
  loading: false,

  setProdutos: (produtos) => set({ produtos }),
  setLoading: (loading) => set({ loading }),

  // Carregar produtos do backend
  carregarProdutos: async () => {
    set({ loading: true });

    // Pega o usuário logado do authStore
    const { user } = useAuthStore.getState();

    try {
      // Decide qual rota chamar com base no tipo de usuário
      const response =
        user?.tipo === "admin"
          ? await api.getAdminProducts()
          : await api.getProducts();

      // Converter dados do backend para formato do frontend
      const produtos = (response.products || []).map((produto) => ({
        ...produto, // Mantém todos os campos originais, incluindo _id
        id: produto._id, // Garante compatibilidade se 'id' for usado em algum lugar
        nome: produto.nome,
        descricao: produto.descricao,
        preco: produto.preco,
        estoque: produto.estoque,
        categoria: produto.categoria,
        dosagem: produto.dosagem,
        fabricante: produto.fabricante,
        imagem: produto.imagem_path || "/assets/produtos/placeholder.jpg",
        // Corrigido para tratar booleano do Mongo e número do MySQL
        ativo: produto.ativo === true || produto.ativo === 1,
        comissaoPercentual: produto.comissao_percentual ?? null,
        comissaoValor: produto.comissao_valor ?? null,
        criadoEm: new Date(produto.criado_em),
        atualizadoEm: new Date(produto.atualizado_em),
      }));

      set({ produtos });
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);

      // Fallback para produtos mock em caso de erro
      const produtosMock = [
        {
          id: "1",
          nome: "Cannabis Oil 10mg/ml",
          descricao:
            "Óleo de cannabis medicinal com concentração de 10mg/ml de CBD",
          preco: 299.9,
          estoque: 50,
          categoria: "Óleos",
          dosagem: "10mg/ml",
          fabricante: "GreenPharma",
          imagem: "/assets/produtos/oleo-10mg.jpg",
          ativo: true,
          criadoEm: new Date(),
          atualizadoEm: new Date(),
        },
        {
          id: "2",
          nome: "Cannabis Oil 20mg/ml",
          descricao:
            "Óleo de cannabis medicinal com concentração de 20mg/ml de CBD",
          preco: 459.9,
          estoque: 30,
          categoria: "Óleos",
          dosagem: "20mg/ml",
          fabricante: "GreenPharma",
          imagem: "/assets/produtos/oleo-20mg.jpg",
          ativo: true,
          criadoEm: new Date(),
          atualizadoEm: new Date(),
        },
        {
          id: "3",
          nome: "Cannabis Extract THC/CBD",
          descricao:
            "Extrato concentrado com THC e CBD para tratamento especializado",
          preco: 699.9,
          estoque: 20,
          categoria: "Extratos",
          dosagem: "THC 5mg/CBD 15mg por ml",
          fabricante: "MediCanna",
          imagem: "/assets/produtos/extrato-thc-cbd.jpg",
          ativo: true,
          criadoEm: new Date(),
          atualizadoEm: new Date(),
        },
        {
          id: "4",
          nome: "Cápsulas CBD 25mg",
          descricao: "Cápsulas de liberação controlada com 25mg de CBD",
          preco: 189.9,
          estoque: 100,
          categoria: "Cápsulas",
          dosagem: "25mg por cápsula",
          fabricante: "CannaWell",
          imagem: "/assets/produtos/capsulas-25mg.jpg",
          ativo: true,
          criadoEm: new Date(),
          atualizadoEm: new Date(),
        },
        {
          id: "5",
          nome: "Gel Tópico CBD",
          descricao: "Gel para aplicação tópica com CBD para alívio localizado",
          preco: 129.9,
          estoque: 75,
          categoria: "Tópicos",
          dosagem: "100mg CBD por tubo",
          fabricante: "GreenTopical",
          imagem: "/assets/produtos/gel-topico.jpg",
          ativo: true,
          criadoEm: new Date(),
          atualizadoEm: new Date(),
        },
      ];

      set({ produtos: produtosMock });
    } finally {
      set({ loading: false });
    }
  },

  // Criar novo produto (Admin only)
  criarProduto: async (tempProductData) => {
    set({ loading: true });

    try {
      // Remove o _id temporário antes de enviar para a API
      const { _id, ...productDataForApi } = tempProductData;
      const response = await api.createProduct(productDataForApi);

      if (!response.success) {
        throw new Error(response.error || "Falha ao criar o produto.");
      }

      // Recarregar lista após criação
      await get().carregarProdutos();
    } catch (error) {
      console.error("Erro ao criar produto:", error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  // Atualizar um produto (Admin only)
  atualizarProduto: async (productId, productData) => {
    set({ loading: true });
    try {
      const response = await api.updateProduct(productId, productData);
      if (response.success) {
        // Atualiza o estado local com os novos dados do produto
        set((state) => ({
          produtos: state.produtos.map((p) =>
            p._id === productId ? { ...p, ...productData, _id: productId } : p
          ),
        }));
      } else {
        throw new Error(response.error || "Falha ao atualizar o produto.");
      }
    } catch (error) {
      console.error("Erro ao atualizar produto:", error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  // Deletar (desativar) um produto (Admin only)
  deletarProduto: async (productId) => {
    set({ loading: true });
    try {
      const response = await api.deleteProduct(productId);
      if (response.success) {
        // Atualiza o estado local marcando o produto como inativo
        set((state) => ({
          produtos: state.produtos.map((p) =>
            p._id === productId ? { ...p, ativo: false } : p
          ),
        }));
      } else {
        throw new Error(response.error || "Falha ao deletar o produto.");
      }
    } catch (error) {
      console.error("Erro ao deletar produto:", error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  adicionarProduto: (novoProduto) => {
    const { produtos } = get();
    set({ produtos: [...produtos, novoProduto] });
  },
}));
